const MOBILE_BREAKPOINT = 768;
const DESKTOP_BREAKPOINT = 1024;

function detectCurrentBreakpoint() {
  const width = window.innerWidth;
  if (width < MOBILE_BREAKPOINT) return 'mobile';
  if (width < DESKTOP_BREAKPOINT) return 'tablet';
  return 'desktop';
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const tag = document.createElement('script');
    tag.src = src;
    tag.async = true;
    tag.defer = true;
    tag.onload = resolve;
    tag.onerror = reject;
    document.body.appendChild(tag);
  });
}

const VIDEO_TYPES = { YOUTUBE: 'youtube', VIMEO: 'vimeo', MP4: 'mp4' };
const STATES = { PLAYING: 'playing', PAUSING: 'pausing', LOADING: 'loading' };

class VideoPlayer extends HTMLElement {
  constructor() {
    super();
    this.player = null;
    this.element = document.createElement('div');
    this.type = this.getAttribute('data-video-type') || VIDEO_TYPES.MP4;
    this.responsive = this.getAttribute('data-video-responsive') || null;
    this.thumbnailEl = null;
    this.isLoaded = false;
    this.src = this.getAttribute('src') || null;
    this.status = { play: null, error: null };
    this.options = {
      autoplay: this.hasAttribute('autoplay'),
      controls: this.hasAttribute('controls'),
      playsinline: this.hasAttribute('playsinline'),
      muted: this.hasAttribute('muted'),
      loop: this.hasAttribute('loop'),
      origin: window.location.origin,
      thumbnail: this.getAttribute('data-thumbnail'),
      playIntersection: this.hasAttribute('data-play-intersection'),
    };

    if (this.type === VIDEO_TYPES.YOUTUBE) {
      this.appendChild(this.element);
    }

    if (!this.src) {
      this.status.error = 'No video src';
    }

    if (this.options.playIntersection) {
      this.initWhenVisible();
    }

    this.unload = this.unload.bind(this);
    this.play = this.play.bind(this);
    this.pause = this.pause.bind(this);
    this.initWhenVisible = this.initWhenVisible.bind(this);

    if (this.hasAttribute('auto-init')) {
      if (this.type === 'vimeo' && this.responsive) {
        this.onResize();
        window.addEventListener('resize', () => this.onResize());
      } else {
        this.init();
      }
    }
  }

  onResize() {
    this.currentBreakpoint = detectCurrentBreakpoint();
    if (
      (this.responsive === 'desktop' && this.currentBreakpoint !== 'mobile') ||
      (this.responsive === 'mobile' && this.currentBreakpoint === 'mobile')
    ) {
      if (!this.classList.contains('initiated')) {
        this.init();
      }
      if (this.options.autoplay) {
        this.play();
      }
    }
  }

  get getVideoID() {
    if (this.getAttribute('data-external-id')) return this.getAttribute('data-external-id');

    if (/youtu\.?be/.test(this.src)) {
      const patterns = [
        /youtu\.be\/([^#&?]{11})/,
        /\?v=([^#&?]{11})/,
        /&v=([^#&?]{11})/,
        /embed\/([^#&?]{11})/,
        /\/v\/([^#&?]{11})/,
      ];
      for (const pattern of patterns) {
        if (pattern.test(this.src)) return pattern.exec(this.src)[1];
      }
    }

    const vimeoMatch = /^.*(vimeo\.com\/)((channels\/[A-z]+\/)|(groups\/[A-z]+\/videos\/))?([0-9]+)/.exec(this.src);
    if (vimeoMatch) return vimeoMatch[5];

    return null;
  }

  initWhenVisible() {
    this.observeVisibility(this).then((isVisible) => {
      if (!this.isLoaded && isVisible) {
        if (this.options.autoplay) {
          this.initThumbnail();
        }
        this.src && this.type && this.init();
        this.isLoaded = true;
      }
    });
  }

  unload() {
    if (this.type === 'youtube') this.player.clearVideo();
    else if (this.type === 'vimeo') this.player.unload();
  }

  initThumbnail() {
    const html = `
      <div class="js-video-player-thumbnail absolute left-0 top-0 w-full h-full flex items-center justify-center transition-opacity" style="background:#555;">
        <img src="${this.options.thumbnail}" loading="lazy" style="width:100%;height:100%;object-fit:cover;">
        <button type="button" class="js-video-player-thumbnail-button" style="position:absolute;width:3rem;height:3rem;border-radius:50%;background:var(--color-primary,#333);display:flex;align-items:center;justify-content:center;border:none;cursor:pointer;">
          <svg width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
            <path d="m28.512 15.15-17.987-11A1 1 0 0 0 9 5v22a1 1 0 0 0 1.525.85l17.987-11a.987.987 0 0 0 0-1.7Z" stroke="#fff" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>`;

    this.thumbnailEl = html;
    this.insertAdjacentHTML('beforeend', this.thumbnailEl);

    const buttonEl = this.querySelector('.js-video-player-thumbnail-button');

    if (this.options.autoplay) this.setStatePlayer(STATES.LOADING);

    buttonEl.addEventListener('click', () => {
      if (!this.player) {
        this.setStatePlayer(STATES.LOADING);
        if (this.type === VIDEO_TYPES.YOUTUBE) this.setupYTPlayer();
        else if (this.type === VIDEO_TYPES.VIMEO) this.setupVimeoPlayer();
        else if (this.type === VIDEO_TYPES.MP4) this.setupMP4Player();
      } else {
        if (!this.status.play) this.play();
        else this.pause();
      }
    });
  }

  initYoutubePlayer() {
    if (!this.options.autoplay && this.options.thumbnail) {
      this.initThumbnail();
    } else {
      YT.ready(() => this.setupYTPlayer());
    }
  }

  initVimeoPlayer() {
    if (!this.options.autoplay && this.options.thumbnail) {
      this.initThumbnail();
    } else {
      this.setupVimeoPlayer();
    }
  }

  initMP4Player() {
    if (this.querySelector('video')) return;
    if (!this.options.autoplay && this.options.thumbnail) {
      this.initThumbnail();
    } else {
      this.setupMP4Player();
    }
  }

  initByType(type) {
    if (type === VIDEO_TYPES.YOUTUBE) this.initYoutubePlayer();
    else if (type === VIDEO_TYPES.VIMEO) this.initVimeoPlayer();
    else if (type === VIDEO_TYPES.MP4) this.initMP4Player();
  }

  async loadPlayerAPIs(type) {
    let src = '';
    if (type === VIDEO_TYPES.YOUTUBE && !window.YT) {
      src = 'https://www.youtube.com/iframe_api';
    } else if (type === VIDEO_TYPES.VIMEO && !window.Vimeo) {
      src = 'https://player.vimeo.com/api/player.js';
    } else {
      return;
    }
    return loadScript(src);
  }

  setupYTPlayer() {
    const options = {
      videoId: this.getVideoID,
      playerVars: {
        autoplay: 0,
        controls: this.options.controls ? 1 : 0,
        height: '100%',
        width: '100%',
        playsinline: this.options.playsinline ? 1 : 0,
        loop: this.options.loop ? 1 : 0,
      },
      events: {
        onError: () => { this.status.error = new Error('player could not be built'); },
        onStateChange: (e) => this._onYouTubeStateChange(e),
        onReady: (e) => this._onYoutubeReadyState(e),
      },
    };
    if (YT.loaded) {
      this.player = new window.YT.Player(this.element, options);
    }
  }

  setupVimeoPlayer() {
    if (this.player) return;
    this.player = new window.Vimeo.Player(this, { id: this.getVideoID, ...this.options });
    this.player.ready().then(() => {
      if (!this.options.autoplay) {
        this.pause();
        this.setStatePlayer(STATES.PLAYING);
        this.displayThumbnail(false);
      } else {
        this.play();
        this.displayThumbnail(false);
      }
    });
    this.player.on('play', () => this.onPlay());
    this.player.on('pause', () => this.onPause());
    this.player.on('ended', () => this.onEnd());
  }

  setupMP4Player() {
    this.videoEl = document.createElement('video');
    this.videoEl.src = this.src;
    this.player = this.videoEl;
    this.appendChild(this.videoEl);
    if (!this.options.autoplay) {
      this.play();
      this.setStatePlayer(STATES.PLAYING);
    }
    this.displayThumbnail(false);
    this.player.controls = this.options.controls;
    this.player.autoplay = this.options.autoplay;
    this.player.playsInline = this.options.playsinline;
    this.player.muted = this.options.muted;
    this.player.loop = this.options.loop;
  }

  play() {
    try {
      if (!this.player) return;
      if (this.type === VIDEO_TYPES.YOUTUBE) this.player.playVideo();
      else this.player.play();
    } catch (error) {
      this.status.error = error;
    }
  }

  pause() {
    try {
      if (!this.player) return;
      if (this.type === VIDEO_TYPES.YOUTUBE) this.player.pauseVideo();
      else this.player.pause();
    } catch (error) {
      this.status.error = error;
    }
  }

  onPlay() {
    this.play();
    this.status.play = true;
    this.dispatchEvent(new CustomEvent('play'));
    this.setStatePlayer(STATES.PLAYING);
    this.displayThumbnail(false);
  }

  onPause() {
    this.pause();
    this.status.play = false;
    this.dispatchEvent(new CustomEvent('pause'));
    this.setStatePlayer(STATES.PAUSING);
    this.displayThumbnail(true);
  }

  onEnd() {
    if (this.options.loop) {
      this.play();
    } else {
      this.status.play = false;
      this.dispatchEvent(new CustomEvent('ended'));
      this.displayThumbnail(true);
      this.setStatePlayer(null);
    }
  }

  setStatePlayer(status) {
    this.classList.remove('is-playing', 'is-pausing', 'is-loading');
    if (status === STATES.PLAYING) this.classList.add('is-playing');
    else if (status === STATES.PAUSING) this.classList.add('is-pausing');
    else if (status === STATES.LOADING) this.classList.add('is-loading');
  }

  displayThumbnail(visible) {
    const el = this.querySelector('.js-video-player-thumbnail');
    if (!el) return;
    if (visible) {
      el.style.opacity = '1';
      el.style.visibility = 'visible';
      el.style.display = '';
    } else {
      el.style.opacity = '0';
      el.style.visibility = 'hidden';
      el.style.display = 'none';
    }
  }

  _onYouTubeStateChange(e) {
    const stateMap = { '-1': 'unstarted', 0: 'ended', 1: 'playing', 2: 'pause', 3: 'buffering', 5: 'cued' };
    const state = stateMap[e.data.toString()];
    const eventMethodMap = { ended: this.onEnd, pause: this.onPause, playing: this.onPlay };
    if (eventMethodMap[state]) eventMethodMap[state].call(this);
  }

  _onYoutubeReadyState() {
    if (!this.options.autoplay) this.setStatePlayer(STATES.PLAYING);
    this.play();
    this.displayThumbnail(false);
    if (this.options.muted) this.player.mute();
  }

  observeVisibility(element) {
    return new Promise((resolve) => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => resolve(entry.isIntersecting));
      });
      observer.observe(element);
    });
  }

  async init() {
    if (this.type !== VIDEO_TYPES.MP4) await this.loadPlayerAPIs(this.type);
    this.initByType(this.type);
    this.classList.add('initiated');
  }
}

if (!customElements.get('video-player')) {
  customElements.define('video-player', VideoPlayer);
}
